import React, { ChangeEvent, MouseEvent, KeyboardEvent, useEffect, useState } from 'react';
import { RootStateOrAny, useSelector } from 'react-redux';

import { changeSelectedFiscalYear, markShown, queryIndexes, queryTabularIndex, updateQueryValue, updateTabularQueryValue } from '../actions/initialActions';
import TabularDataModal from './TabularDataModal';
import { Button, Row, Col, FormGroup, Label, Input } from 'reactstrap';
import { isEmpty } from 'lodash';

let timeoutId: NodeJS.Timeout;

const Header = (props: { modal: boolean, toggleModal: Function }) => {
    const fiscalYears = useSelector((state: RootStateOrAny) => state.metadata.fiscal_years)
    const selectedFiscalYear = useSelector((state: RootStateOrAny) => state.selectedFiscalYear)
    const diseaseInjuriesCount = useSelector((state: RootStateOrAny) => state.diseaseInjuriesCount)
    const externalCausesCount = useSelector((state: RootStateOrAny) => state.externalCausesCount)
    const drugsTableCount = useSelector((state: RootStateOrAny) => state.drugsTableCount)
    const neoplasmsTableCount = useSelector((state: RootStateOrAny) => state.neoplasmsTableCount)
    const query = useSelector((state: RootStateOrAny) => state.query)
    const fy = useSelector((state: RootStateOrAny) => state.fy)
    const tabularQuery = useSelector((state: RootStateOrAny) => state.tabularQuery)
    const showImmediately = useSelector((state: RootStateOrAny) => state.showImmediately)
    const externalCausesQueryInProgress = useSelector((state: RootStateOrAny) => state.externalCausesQueryInProgress)
    const diseaseInjuriesQueryInProgress = useSelector((state: RootStateOrAny) => state.diseaseInjuriesQueryInProgress)
    const drugsTableQueryInProgress = useSelector((state: RootStateOrAny) => state.drugsTableQueryInProgress)
    const neoplasmsTableQueryInProgress = useSelector((state: RootStateOrAny) => state.neoplasmsTableQueryInProgress)
    const errors = useSelector((state: RootStateOrAny) => state.errors)

    const [activeNav, setActiveNav] = useState('')

    const fiscalYearOptions = fiscalYears ? fiscalYears.map((year: { fiscal_year: string, description: string }) => {
        return <option key={year.fiscal_year} value={year.fiscal_year}>{year.fiscal_year} - {year.description}</option>
    }) : [];

    const handleChangeFiscalYear = (event: ChangeEvent<HTMLSelectElement>) => {
        changeSelectedFiscalYear(event.target.value, query);
    }

    const handleSearchInput = (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        updateQueryValue(newValue);
        if (newValue.length > 2) {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                queryIndexes(newValue, fy);
            }, 1000)
        }
    }

    // TODO This probably needs work. Scroll to needs to be updated
    const handleNavBarClick = (event: MouseEvent) => {
        const el = event.target as HTMLLinkElement;
        setActiveNav(el.id);
    }

    const handleClick = (event: MouseEvent) => {
        event.preventDefault();
        getTabularData();
    }

    const handleEnterKey = (event: KeyboardEvent) => {
        event.preventDefault();
        if (event.key === "Enter") {
            getTabularData();
        }
    }

    const getTabularData = () => {
        queryTabularIndex(tabularQuery, fy).then(() => props.toggleModal())
    }

    const handleTabularSearchInput = (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value.toUpperCase();
        updateTabularQueryValue(newValue);
    }

    const errorMessages = errors.map((error: { message: string, status: string }) => {
        return (<span key={error.message}>{error.message}<br/></span>)
    })

    useEffect(() => {
        if (showImmediately) {
            markShown();
            getTabularData();
        }
    })

    return (
        <header role="banner" className="container-fluid sticky-top search-header">
          <div className="primary subheader pl-3">
            <a href="https://www.cdc.gov/nchs/index.htm">National Center for Health Statistics</a> &#8211; ICD&#8288;-&#8288;10&#8288;-&#8288;CM
          </div>
            { isEmpty(errors) ? ''
            :
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
                {errorMessages}
            </div>
            }
            <div className="search-form">
              <Row form>
                <Col>
                  <FormGroup className="form-inline">
                    <Label for="fiscalYear">Fiscal Year:&nbsp;&nbsp;</Label>
                    <select id="fiscalYear" className="searchbar form-control form-control-sm" onChange={handleChangeFiscalYear} value={selectedFiscalYear.fiscal_year}>
                      <option disabled value="Select Fiscal Year">Select Fiscal Year:&nbsp;&nbsp;</option>
                      {fiscalYearOptions}
                    </select>
                  </FormGroup>
                </Col>
              </Row>
              <Row form>
                <Col>
                  <FormGroup className="form-inline">
                    <Label for="termSearch">Enter Search Term(s):&nbsp;&nbsp;</Label>
                    <Input
                      className="searchbar form-control-sm"
                      type="search"
                      name="termSearch"
                      id="termSearch"
                      placeholder="Enter Search Term(s)"
                      autoFocus
                      onChange={handleSearchInput}
                      value={query}
                      disabled={externalCausesQueryInProgress || diseaseInjuriesQueryInProgress || drugsTableQueryInProgress || neoplasmsTableQueryInProgress}
                    />
                  </FormGroup>
                </Col>
                <Col>
                  <FormGroup className="form-inline">
                    <Label for="tableSearch">Find code in Tabular:&nbsp;&nbsp;</Label>
                    <div>
                      <Input
                        type="text"
                        className="textinput input-button-border form-control-sm"
                        id="tableSearch"
                        placeholder="Enter ICD-10 Code"
                        onChange={handleTabularSearchInput}
                        onKeyUp={handleEnterKey}
                        value={tabularQuery}
                      />
                      <Button className="icdbutton px-3" color="primary" size="sm" disabled={tabularQuery.length > 2 ? false : true} onClick={handleClick}>View</Button>
                    </div>
                    <TabularDataModal modal={props.modal} toggleModal={props.toggleModal} />
                  </FormGroup>
                </Col>
              </Row>
            </div>
            <h2 className="sr-only">Results</h2>
            <Row form className="results-options">
              <Col>
                <Button size="sm" href="#disease-injuries" id="disease-injuries-nav" color="outline-primary" onClick={handleNavBarClick} className={`w-100 ${activeNav === 'disease-injuries-nav' ? 'active' : ''} ${diseaseInjuriesCount === 0 ? 'disabled' : ''}`}>
                  Disease Index <span className={`badge badge-pill ${activeNav === 'disease-injuries-nav' ? 'badge-light' : 'badge-secondary'}`}>{diseaseInjuriesCount === null ? 0 : diseaseInjuriesCount}</span><span className="sr-only"> results</span>
                </Button>
              </Col>
              <Col>
                <Button size="sm" href="#external-causes" id="external-causes-nav" color="outline-primary" onClick={handleNavBarClick} className={`w-100 ${activeNav === 'external-causes-nav' ? 'active' : ''} ${externalCausesCount === 0 ? 'disabled' : ''}`}>
                  External Causes <span className={`badge badge-pill ${activeNav === 'external-causes-nav' ? 'badge-light' : 'badge-secondary'}`}>{externalCausesCount === null ? 0 : externalCausesCount}</span><span className="sr-only"> results</span>
                </Button>
              </Col>
              <Col>
                <Button size="sm" href="#drugs-table" id="drugs-table-nav" color="outline-primary" onClick={handleNavBarClick} className={`w-100 ${activeNav === 'drugs-table-nav' ? 'active' : ''} ${drugsTableCount === 0 ? 'disabled' : ''}`}>
                  Drugs <span className={`badge badge-pill ${activeNav === 'drugs-table-nav' ? 'badge-light' : 'badge-secondary'}`}>{drugsTableCount === null ? 0 : drugsTableCount}</span><span className="sr-only"> results</span>
                </Button>
              </Col>
              <Col>
                <Button size="sm" href="#neoplasms-table" id="neoplasms-table-nav" color="outline-primary" onClick={handleNavBarClick} className={`w-100 ${activeNav === 'neoplasms-table-nav' ? 'active' : ''} ${neoplasmsTableCount === 0 ? 'disabled' : ''}`}>
                  Neoplasms <span className={`badge badge-pill ${activeNav === 'neoplasms-table-nav' ? 'badge-light' : 'badge-secondary'}`}>{neoplasmsTableCount === null ? 0 : neoplasmsTableCount}</span><span className="sr-only"> results</span>
                </Button>
              </Col>
            </Row>
        </header>
    );
}

export default Header;
